import { WageFrequency } from "@api/shared-runtime/enums";
import {
  WAGE_AMOUNT_PATTERN,
  type PayWageFrequency,
} from "@api/shared-runtime/pay-wages";
import type { RouterInputs, RouterOutputs } from "@/trpc";
import { formatWage } from "@/features/time-entry/utils/custom-wage";

export type WageInput = RouterInputs["organizationWageSet"]["create"]["wages"];
export type OrganizationWageSetWages = NonNullable<
  RouterOutputs["organizationWageSet"]["list"][number]["latestVersion"]
>["wages"];

export type WageFieldKey =
  | "regularWage"
  | "regularOvertimeWage"
  | "weekendWage"
  | "weekendOvertimeWage"
  | "onCallWage"
  | "onCallOvertimeWage"
  | "onCallCallbackWage"
  | "onCallCallbackOvertimeWage"
  | "holidayWage"
  | "holidayOvertimeWage";

export type WageFieldDefinition = {
  key: WageFieldKey;
  label: string;
  isOvertime: boolean;
};

export type WageFormValues = {
  wageFrequencyId: PayWageFrequency;
  amounts: Record<WageFieldKey, string>;
};

export type WageFormErrors = Partial<Record<WageFieldKey, string>>;

export const WAGE_FIELDS: WageFieldDefinition[] = [
  { key: "regularWage", label: "Regular", isOvertime: false },
  { key: "regularOvertimeWage", label: "Regular overtime", isOvertime: true },
  { key: "weekendWage", label: "Weekend", isOvertime: false },
  { key: "weekendOvertimeWage", label: "Weekend overtime", isOvertime: true },
  { key: "onCallWage", label: "On call", isOvertime: false },
  { key: "onCallOvertimeWage", label: "On call overtime", isOvertime: true },
  { key: "onCallCallbackWage", label: "On-call callback", isOvertime: false },
  {
    key: "onCallCallbackOvertimeWage",
    label: "On-call callback overtime",
    isOvertime: true,
  },
  { key: "holidayWage", label: "Holiday", isOvertime: false },
  { key: "holidayOvertimeWage", label: "Holiday overtime", isOvertime: true },
];

const REGULAR_RATE_KEY: WageFieldKey = "regularWage";

const SUMMARY_SEPARATOR = " · ";
const AMOUNT_ERROR = "Enter a dollar amount with up to 2 decimals";
const REGULAR_REQUIRED_ERROR = "Enter a regular wage greater than 0";

function emptyAmounts(): Record<WageFieldKey, string> {
  const amounts = {} as Record<WageFieldKey, string>;
  for (const field of WAGE_FIELDS) {
    amounts[field.key] = "";
  }
  return amounts;
}

export function emptyWageFormValues(): WageFormValues {
  return { wageFrequencyId: WageFrequency.Hourly, amounts: emptyAmounts() };
}

function isPayWageFrequency(value: string): value is PayWageFrequency {
  return value === WageFrequency.Hourly || value === WageFrequency.OneTime;
}

/** Prefills the form from stored wages, e.g. when changing a wage set's wages. */
export function wageFormValuesFromWages(
  wages: OrganizationWageSetWages,
): WageFormValues {
  const amounts = emptyAmounts();
  for (const field of WAGE_FIELDS) {
    amounts[field.key] = wages[field.key] ?? "";
  }
  return {
    wageFrequencyId: isPayWageFrequency(wages.wageFrequencyId)
      ? wages.wageFrequencyId
      : WageFrequency.Hourly,
    amounts,
  };
}

export function isOneTimeFrequency(wageFrequencyId: PayWageFrequency) {
  return wageFrequencyId === WageFrequency.OneTime;
}

/** Fields the form shows: per-shift pay has no overtime wages. */
export function getVisibleWageFields(
  wageFrequencyId: PayWageFrequency,
): WageFieldDefinition[] {
  if (isOneTimeFrequency(wageFrequencyId)) {
    return WAGE_FIELDS.filter((field) => !field.isOvertime);
  }
  return WAGE_FIELDS;
}

export function validateWageForm(values: WageFormValues): WageFormErrors {
  const errors: WageFormErrors = {};
  for (const field of getVisibleWageFields(values.wageFrequencyId)) {
    const amount = values.amounts[field.key].trim();
    if (field.key === REGULAR_RATE_KEY) {
      if (!WAGE_AMOUNT_PATTERN.test(amount) || Number(amount) <= 0) {
        errors[field.key] = REGULAR_REQUIRED_ERROR;
      }
    } else if (amount !== "" && !WAGE_AMOUNT_PATTERN.test(amount)) {
      errors[field.key] = AMOUNT_ERROR;
    }
  }
  return errors;
}

/** Builds the API input from a form that already passed validation. */
export function wageFormValuesToInput(values: WageFormValues): WageInput {
  const input: WageInput = {
    wageFrequencyId: values.wageFrequencyId,
    regularWage: values.amounts.regularWage.trim(),
  };
  for (const field of getVisibleWageFields(values.wageFrequencyId)) {
    const amount = values.amounts[field.key].trim();
    if (field.key !== REGULAR_RATE_KEY && amount !== "") {
      input[field.key] = amount;
    }
  }
  return input;
}

/** Every wage set on a version, with its label and formatted amount. */
export function listWageLines(
  wages: OrganizationWageSetWages,
): { key: WageFieldKey; label: string; display: string }[] {
  const wageFrequencyId = isPayWageFrequency(wages.wageFrequencyId)
    ? wages.wageFrequencyId
    : WageFrequency.Hourly;
  const lines: { key: WageFieldKey; label: string; display: string }[] = [];
  for (const field of WAGE_FIELDS) {
    const amount = wages[field.key];
    if (amount !== null && amount !== undefined) {
      lines.push({
        key: field.key,
        label: field.label,
        display: formatWage({ amount, wageFrequencyId }),
      });
    }
  }
  return lines;
}

/**
 * One-line summary of every wage the set has, such as
 * "$100.00/hr · Regular overtime $150.00/hr · On call $50.00/hr". Overtime
 * wages are included so reviewers can tell which sets can pay overtime hours.
 */
export function formatWageSummary(wages: OrganizationWageSetWages): string {
  return listWageLines(wages)
    .map((line) =>
      line.key === REGULAR_RATE_KEY
        ? line.display
        : `${line.label} ${line.display}`,
    )
    .join(SUMMARY_SEPARATOR);
}
